import { Navigate, useLocation } from 'react-router-dom';
import { useIsAdmin } from '@/hooks/useUserRole';
import { useAuth } from '@/contexts/AuthContext';
import { useMfa } from '@/hooks/useMfa';
import { MfaEnroll } from '@/components/auth/MfaEnroll';
import { MfaVerify } from '@/components/auth/MfaVerify';
import { Loader2 } from 'lucide-react';

const REQUIRE_ADMIN_MFA = import.meta.env.VITE_REQUIRE_ADMIN_MFA !== 'false';

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { loading: authLoading } = useAuth();
  const { isAdmin, isLoading } = useIsAdmin();
  const { currentLevel, nextLevel, isLoading: mfaLoading, refresh } = useMfa();
  const location = useLocation();

  if (authLoading || isLoading || mfaLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/home" state={{ from: location }} replace />;
  }

  // MFA enforcement for admins
  if (REQUIRE_ADMIN_MFA) {
    // No MFA factor enrolled — force enrollment
    if (nextLevel === 'aal1') {
      return (
        <MfaEnroll
          onSuccess={() => refresh()}
        />
      );
    }

    // MFA enrolled but not verified this session — force verify
    if (currentLevel === 'aal1' && nextLevel === 'aal2') {
      return (
        <MfaVerify
          onSuccess={() => refresh()}
        />
      );
    }

    // currentLevel === 'aal2' — fully verified, continue
  }

  return <>{children}</>;
}
